<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import debounce from 'lodash/debounce';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: Boolean,
  message: { type: Object, default: null },
  messages: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'update:show']);

const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

const store = useStore();
const { t } = useI18n();
const searchQuery = ref('');
const isForwarding = ref(false);

// Determine if we're forwarding multiple messages
const isMultipleMessages = computed(
  () => props.messages && props.messages.length > 0
);

const modalTitle = computed(() => {
  if (isMultipleMessages.value && props.messages.length > 1) {
    return t('CONVERSATION.FORWARD_MODAL.TITLE_MULTIPLE', {
      count: props.messages.length,
    });
  }
  return t('CONVERSATION.FORWARD_MODAL.TITLE');
});

// Get conversation ID from message(s)
const conversationId = computed(() => {
  if (isMultipleMessages.value && props.messages.length > 0) {
    return (
      props.messages[0].conversation_id || props.messages[0].conversationId
    );
  }
  return props.message?.conversation_id || props.message?.conversationId;
});

// Get message IDs to forward
const messageIds = computed(() => {
  if (isMultipleMessages.value) {
    return props.messages.map(m => m.id);
  }
  return props.message ? [props.message.id] : [];
});

const contacts = computed(() => store.getters['contacts/getContacts']);
const showEmptySearchResult = computed(
  () => !!searchQuery.value && contacts.value.length === 0
);

function updatePageParam(page) {
  window.history.pushState(
    {},
    null,
    `${window.location.pathname}?page=${page}`
  );
}

function onClose() {
  // Exit selection mode if it was active
  store.dispatch('messageSelection/exitSelectionMode');
  emit('update:show', false);
  emit('close');
}

function fetchContacts(page) {
  updatePageParam(page);
  let value = searchQuery.value;
  if (value.startsWith('+')) {
    value = value.substring(1);
  }
  const requestParams = { page, sortAttr: '-last_activity_at' };
  if (!value) {
    store.dispatch('contacts/get', requestParams);
  } else {
    store.dispatch('contacts/search', {
      search: encodeURIComponent(value),
      ...requestParams,
    });
  }
}

const onInputSearch = debounce(event => {
  searchQuery.value = event.target.value;
  fetchContacts(1);
}, 300);

function resetSearch(event) {
  const newQuery = event.target.value;
  if (!newQuery) {
    searchQuery.value = newQuery;
    fetchContacts(1);
  }
}

async function onSubmit(event) {
  const formData = new FormData(event.target);
  const contactIds = formData.getAll('contactIds[]');

  if (contactIds.length === 0) {
    useAlert(t('CONVERSATION.FORWARD_MODAL.SELECT_CONVERSATION'));
    return;
  }

  isForwarding.value = true;

  try {
    if (isMultipleMessages.value) {
      // Forward multiple messages
      await store.dispatch('forwardMessages', {
        conversationId: conversationId.value,
        messageIds: messageIds.value,
        contacts: contactIds,
      });
      useAlert(
        t('CONVERSATION.FORWARD_MODAL.SUCCESS_MULTIPLE', {
          count: messageIds.value.length,
        })
      );
    } else {
      // Forward single message
      await store.dispatch('forwardMessage', {
        conversationId: conversationId.value,
        messageId: messageIds.value[0],
        contacts: contactIds,
      });
      useAlert(t('CONVERSATION.FORWARD_MODAL.SUCCESS'));
    }
    onClose();
  } catch (error) {
    useAlert(t('CONVERSATION.FORWARD_MODAL.ERROR'));
  } finally {
    isForwarding.value = false;
  }
}

onMounted(() => {
  fetchContacts(1);
});
</script>

<template>
  <woot-modal
    v-model:show="showModal"
    modal-type="right-aligned"
    :on-close="onClose"
  >
    <div
      class="fixed inset-0 sm:inset-y-0 sm:right-0 sm:left-auto max-h-screen overflow-y-auto overflow-x-hidden bg-white shadow-md modal-container rtl:text-right dark:bg-slate-900 skip-context-menu rounded-none sm:rounded-l-xl w-full max-w-[100vw] sm:w-[28rem] md:w-[36rem] lg:w-[42rem] xl:w-[50rem] z-50 box-border"
    >
      <div
        class="w-full box-border px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-800 relative"
      >
        <NextButton
          icon="i-lucide-x"
          ghost
          slate
          sm
          :aria-label="t('GENERAL.CLOSE')"
          class="absolute top-3 ltr:right-4 rtl:left-4 sm:ltr:right-6 sm:rtl:left-6"
          @click="onClose"
        />
        <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
          {{ modalTitle }}
        </h1>
        <p class="mt-1 text-xs text-slate-600 dark:text-slate-400">
          {{
            isMultipleMessages && messages.length > 1
              ? t('CONVERSATION.FORWARD_MODAL.MESSAGES_SELECTED', {
                  count: messages.length,
                })
              : t('CONVERSATION.FORWARD_MODAL.DESC')
          }}
        </p>
      </div>
      <div
        class="w-full box-border px-4 sm:px-6 py-3 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-20 bg-white dark:bg-slate-900"
      >
        <label
          class="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1"
        >
          {{ t('CONVERSATION.FORWARD_MODAL.SEARCH_LABEL') }}
        </label>
        <input
          v-model="searchQuery"
          type="search"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white hover:border-woot-500 transition-colors text-sm"
          :placeholder="t('CONVERSATION.FORWARD_MODAL.SEARCH_PLACEHOLDER')"
          @input="onInputSearch"
          @search="resetSearch"
        />
      </div>
      <form class="flex-1 flex flex-col min-h-0" @submit.prevent="onSubmit">
        <div
          class="w-full box-border px-4 sm:px-6 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 hidden sm:block"
        >
          <div
            class="flex items-center text-xs font-medium text-slate-700 dark:text-slate-300"
          >
            <div class="w-6" />
            <div class="flex-1 min-w-[150px]">
              {{ t('CONVERSATION.FORWARD_MODAL.CONTACT') }}
            </div>
            <div class="w-36">{{ t('CONVERSATION.FORWARD_MODAL.PHONE') }}</div>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto min-h-0 w-full box-border">
          <div class="divide-y divide-slate-200 dark:divide-slate-700">
            <div
              v-for="contact in contacts"
              :key="contact.id"
              class="flex items-center flex-wrap w-full box-border px-4 sm:px-6 py-2 hover:bg-slate-800/5 dark:hover:bg-slate-800"
            >
              <div class="w-6">
                <input
                  type="checkbox"
                  :value="contact.id"
                  name="contactIds[]"
                  class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500/20"
                />
              </div>
              <div class="flex-1 min-w-0 flex items-center gap-2">
                <div v-if="contact.thumbnail" class="w-6 h-6">
                  <img
                    :src="contact.thumbnail"
                    :alt="contact.name"
                    class="w-full h-full rounded-full object-cover"
                  />
                </div>
                <span class="truncate text-xs">{{ contact.name }}</span>
              </div>
              <div
                class="w-full sm:w-36 truncate text-xs tracking-tight opacity-80 mt-1 sm:mt-0"
              >
                {{ contact.phone_number }}
              </div>
            </div>
          </div>
        </div>
        <div
          class="w-full box-border px-4 sm:px-6 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end sticky bottom-0 z-20"
        >
          <NextButton
            :disabled="showEmptySearchResult || isForwarding"
            class="bg-woot-500 hover:bg-woot-600 text-white text-sm px-4 py-1.5 w-full sm:w-auto justify-center"
          >
            {{
              isForwarding
                ? t('CONVERSATION.FORWARD_MODAL.FORWARDING')
                : t('CONVERSATION.FORWARD_MODAL.FORWARD')
            }}
          </NextButton>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
